import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import type { ArtJobResponse, ArtImageResponse } from "@/lib/api-types"
import { Loader2, Edit2, Download } from "lucide-react"
import { useArtJob } from "@/hooks/useArtJob"
import { apiClient } from "@/lib/api-client"
import { useToast } from "@/hooks/use-toast"
import { LuckyVariationsGrid } from "@/components/lucky-variations-grid"
import React from "react"

// ✅ Componente de imagem otimizado com retry automático (forwardRef)
const OptimizedImage = React.forwardRef<HTMLImageElement, React.ImgHTMLAttributes<HTMLImageElement>>(
  ({ src, alt, ...props }, ref) => {
    const [imageSrc, setImageSrc] = useState(src)
    const [retryCount, setRetryCount] = useState(0)
    const MAX_RETRIES = 3

    const handleError = () => {
      if (retryCount < MAX_RETRIES) {
        console.warn(`[IMAGE] Retry ${retryCount + 1}/${MAX_RETRIES}`)
        setRetryCount(retryCount + 1)
        // Força reload adicionando timestamp
        setImageSrc(`${src}${src?.includes('?') ? '&' : '?'}t=${Date.now()}`)
      }
    }

    return (
      <img
        ref={ref}
        src={imageSrc}
        alt={alt}
        {...props}
        onError={handleError}
        loading="lazy"
        decoding="async"
      />
    )
  }
)

OptimizedImage.displayName = "OptimizedImage"

export default function ResultPage() {
  const navigate = useNavigate()
  const { artId } = useParams<{ artId: string }>()
  const [art, setArt] = useState<ArtJobResponse | null>(null)
  const [artImages, setArtImages] = useState<ArtImageResponse[]>([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isRerolling, setIsRerolling] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showRerollDialog, setShowRerollDialog] = useState(false)
  const [rerollPrompt, setRerollPrompt] = useState("")
  const [editPrompt, setEditPrompt] = useState("")
  const [isEditRegenerating, setIsEditRegenerating] = useState(false)
  // 💜 Modo Sonho da Sorte
  const [showSonhoDialog, setShowSonhoDialog] = useState(false)
  const [isSonhoRegenerating, setIsSonhoRegenerating] = useState(false)
  const [sonhoPrompt, setSonhoPrompt] = useState("")
  // ✅ Zoom com 2 dedos
  const [zoom, setZoom] = useState(1)
  const [panX, setPanX] = useState(0)
  const [panY, setPanY] = useState(0)
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const { createArtJob, pollArtJob, regenerateArtJob, editArtJob } = useArtJob()
  const { toast } = useToast()

  // ✅ Handlers para pinch-zoom (2 dedos no celular)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch-zoom: calcular distância inicial entre 2 dedos
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )
      setPanStart({ x: distance, y: 0 })
    } else if (e.touches.length === 1) {
      // Pan com 1 dedo quando zoomed
      setIsPanning(true)
      setPanStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch-zoom em progresso
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      )
      
      const newZoom = Math.min(Math.max(distance / panStart.x * zoom, 1), 4)
      setZoom(newZoom)
    } else if (e.touches.length === 1 && isPanning && zoom > 1) {
      // Pan quando zoomed
      const dx = e.touches[0].clientX - panStart.x
      const dy = e.touches[0].clientY - panStart.y
      setPanX(panX + dx)
      setPanY(panY + dy)
      setPanStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    }
  }

  const handleTouchEnd = () => {
    setIsPanning(false)
  }

  // Reset zoom quando volta a 1x
  const handleResetZoom = () => {
    setZoom(1)
    setPanX(0)
    setPanY(0)
  }

  // ✅ NOVO: Carrega a arte quando o componente monta
  useEffect(() => {
    const loadArt = async () => {
      if (!artId) {
        setLoading(false)
        return
      }
      
      try {
        setLoading(true)
        
        const response = await apiClient.getArtJob(artId)
        
        const artData = response?.data
        
        if (artData && artData.id) {
          setArt(artData)
          
          // Se for modo Lucky, buscar ArtImages
          if (artData.modo_lucky) {
            setLoadingImages(true)
            try {
              const imagesResponse = await apiClient.listArtJobImages(artId)
              setArtImages(imagesResponse.data)
            } catch (error: any) {
              console.error('[ERROR] Error loading art images:', error)
              // Não falha a página se não conseguir carregar imagens
            } finally {
              setLoadingImages(false)
            }
          }
        } else {
          throw new Error('Invalid art data')
        }
      } catch (error: any) {
        console.error('[ERROR] Error loading art:', error)
        toast({
          title: "Erro ao carregar",
          description: error.response?.data?.detail || error.message || "Não foi possível carregar a arte",
          variant: "destructive"
        })
        navigate("/")
      } finally {
        setLoading(false)
      }
    }
    
    loadArt()
  }, [artId, navigate, toast])

  // ✅ Force render quando arte muda (reroll/editar)
  useEffect(() => {
    if (art && art.id) {
      // Reset zoom pra renderizar rápido
      setZoom(1)
      setPanX(0)
      setPanY(0)
      // Scroll pra cima logo (mais rápido que smooth)
      window.scrollTo({ top: 0, behavior: "instant" })
    }
  }, [art?.id])

  const handleReroll = async () => {
    if (!art) return
    // Abre o dialog para o user digitar um prompt opcional
    setShowRerollDialog(true)
    setRerollPrompt("")
  }

  const handleConfirmReroll = async () => {
    if (!art) return
    setIsRerolling(true)
    setShowRerollDialog(false)
    try {
      // ✅ Chamar API direto para ser mais rápido (sem passar por hook)
      const response = await apiClient.rerollArtJob(art.id, rerollPrompt || undefined)
      const newJob = response.data
      
      if (!newJob || !newJob.id) {
        throw new Error("Erro ao criar novo job")
      }
      
      toast({
        title: "Gerando variação...",
        description: rerollPrompt ? `Com sua mudança: "${rerollPrompt}"` : "Gerando variação pura...",
      })
      
      // ✅ Poll o novo job com intervalo otimizado
      const completedJob = await pollArtJob(newJob.id)
      
      if (completedJob && completedJob.status === "succeeded") {
        // Garantir que temos os dados mais recentes da API
        const freshArt = await apiClient.getArtJob(completedJob.id)
        if (freshArt && freshArt.data) {
          setArt(freshArt.data)
        } else {
          setArt(completedJob)
        }
        
        setRerollPrompt("")
        toast({
          title: "Sucesso!",
          description: "Nova variação gerada",
        })
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } catch (error) {
      console.error("Reroll failed:", error)
      toast({
        title: "Erro ao refazer",
        description: error instanceof Error ? error.message : "Não foi possível gerar uma nova variação",
        variant: "destructive",
      })
    } finally {
      setIsRerolling(false)
    }
  }

  const handleEditPrompt = async () => {
    if (!art || !editPrompt.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, insira um novo prompt",
        variant: "destructive",
      })
      return
    }

    setIsEditRegenerating(true)
    try {
      console.log("[Edit] Iniciando edição com prompt:", editPrompt)
      
      // Chamar novo endpoint que:
      // 1. Descreve a imagem atual com Gemini Vision
      // 2. Combina com o prompt de edição
      // 3. Gera nova arte
      const newJob = await editArtJob(art.id, editPrompt)

      if (!newJob || !newJob.id) {
        throw new Error("Erro ao criar novo job")
      }

      toast({
        title: "Gerando arte editada...",
        description: `Analisando imagem atual + sua edição...`,
      })

      // Poll the new job
      const completedJob = await pollArtJob(newJob.id)

      if (completedJob && completedJob.status === "succeeded") {
        console.log("[Edit] Novo job completado:", completedJob)
        
        // Garantir que temos os dados mais recentes da API
        const freshArt = await apiClient.getArtJob(completedJob.id)
        if (freshArt && freshArt.data) {
          console.log("[Edit] Arte atualizada da API:", freshArt.data)
          setArt(freshArt.data)
        } else {
          setArt(completedJob)
        }
        
        setIsEditing(false)
        setEditPrompt("")
        toast({
          title: "Sucesso!",
          description: "Arte editada com sucesso!",
        })
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } catch (error) {
      console.error("Edit prompt failed:", error)
      toast({
        title: "Erro ao editar",
        description: error instanceof Error ? error.message : "Não foi possível editar a arte",
        variant: "destructive",
      })
    } finally {
      setIsEditRegenerating(false)
    }
  }

  // 💜 Handler para aplicar Modo Sonho da Sorte
  const handleConfirmSonho = async () => {
    if (!art) return
    
    setIsSonhoRegenerating(true)
    setShowSonhoDialog(false)
    
    try {
      // Prompt para recolor roxo - foco em fundo roxo
      let finalSonhoPrompt = `Recolore a imagem para a identidade visual Sonho da Sorte. 
INSTRUÇÕES CRÍTICAS:
- Fundo: deve ser ROXO (#7B4FB0) ou tons de roxo
- Cores principais: tender para roxo e amarelo (#FFD700)
- Mantenha o sujeito principal (pessoa/animal/objeto) reconhecível
- Aplique roxo em fundo, sombras, destaques secundários
- O resultado final deve TER MUITO ROXO como cor dominante`
      
      // APPEND: adicionar prompt do usuário se fornecido
      if (sonhoPrompt.trim()) {
        finalSonhoPrompt += `\n\nADICIONALMENTE: ${sonhoPrompt}`
      }
      
      console.log('[SONHO DA SORTE] Aplicando filtro na arte gerada')
      
      toast({
        title: "Aplicando Sonho da Sorte...",
        description: "Recolorindo com roxo e amarelo",
      })
      
      // Usar a imagem gerada como input para o recolor
      const s3_url = art.s3_url
      if (!s3_url) {
        throw new Error("Erro ao obter URL da imagem")
      }
      
      // Criar novo job de arte com a imagem como referência
      const response = await apiClient.createArtJob({
        prompt: finalSonhoPrompt,
        seed: undefined,
        input_photos: [s3_url], // Usar imagem gerada como input
        width: art.width || 1024,
        height: art.height || 1024,
        mode: 'generate'
      })
      
      const newJob = response.data
      
      if (!newJob || !newJob.id) {
        throw new Error("Erro ao criar job de recolor")
      }
      
      // Poll o novo job
      const completedJob = await pollArtJob(newJob.id)
      
      if (completedJob && completedJob.status === "succeeded") {
        // Garantir que temos os dados mais recentes
        const freshArt = await apiClient.getArtJob(completedJob.id)
        if (freshArt && freshArt.data) {
          setArt(freshArt.data)
        } else {
          setArt(completedJob)
        }
        
        setSonhoPrompt("")
        toast({
          title: "Sucesso!",
          description: "Modo Sonho da Sorte aplicado com sucesso!",
        })
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } catch (error) {
      console.error("Sonho da Sorte failed:", error)
      toast({
        title: "Erro ao aplicar Sonho da Sorte",
        description: error instanceof Error ? error.message : "Não foi possível aplicar o filtro",
        variant: "destructive",
      })
    } finally {
      setIsSonhoRegenerating(false)
    }
  }

  const getImageDimensions = () => {
    if (!art?.width || !art?.height) return { width: "100%", height: "400px" }
    
    // Respeitar proporção real da imagem
    if (window.innerWidth < 640) {
      // Mobile: aumentar para 95% da largura, deixar altura livre
      return {
        width: "95%",
        height: "auto",
        maxWidth: "600px"
      }
    } else {
      // Desktop: aumentar para 85% da altura da viewport
      const maxHeightPx = window.innerHeight * 0.85 // 85% da viewport height (bem maior!)
      const ratio = art.width / art.height
      const calculatedWidth = maxHeightPx * ratio
      
      return {
        width: Math.min(calculatedWidth, window.innerWidth * 0.85),
        height: "auto"
      }
    }
  }

  if (loading || !art) {
    return (
      <div className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-purple-300 text-sm">Carregando arte...</p>
        </div>
      </div>
    )
  }

  const imageDimensions = getImageDimensions()
  const isLuckyMode = art.modo_lucky === true

  // Função para recarregar imagens após regeneração
  const handleReloadImages = async () => {
    if (!artId || !isLuckyMode) return
    
    setLoadingImages(true)
    try {
      const imagesResponse = await apiClient.listArtJobImages(artId)
      setArtImages(imagesResponse.data)
      
      // Também recarrega o job para pegar ZIP atualizado
      const jobResponse = await apiClient.getArtJob(artId)
      if (jobResponse.data) {
        setArt(jobResponse.data)
      }
    } catch (error: any) {
      console.error('[ERROR] Error reloading images:', error)
    } finally {
      setLoadingImages(false)
    }
  }


  return (
    <div className="w-full flex flex-col items-center py-4 sm:py-8 px-2 sm:px-8 min-h-screen">
      <Card className="w-full max-w-6xl p-3 sm:p-12 shadow-2xl border-purple-500/20 bg-gradient-to-br from-slate-900/95 to-purple-900/40 backdrop-blur-sm mt-4 sm:mt-0">
        <div className="space-y-3 sm:space-y-8">
          <div className="text-center space-y-1 sm:space-y-3">
            <h1 className="text-2xl sm:text-5xl font-bold tracking-tight bg-gradient-to-r from-purple-300 via-pink-300 to-purple-200 bg-clip-text text-transparent">
              {isLuckyMode ? "🍀 Variações Lucky Geradas" : "Arte Gerada"}
            </h1>
            <p className="text-purple-100 text-sm sm:text-xl">
              {isLuckyMode ? "Todas as variações foram geradas com sucesso" : "Sua arte foi criada com sucesso"}
            </p>
          </div>

          {/* Modo Lucky: Exibir grid de variações */}
          {isLuckyMode ? (
            <div className="space-y-4">
              {loadingImages ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                  <span className="ml-3 text-purple-200">Carregando variações...</span>
                </div>
              ) : artImages.length > 0 ? (
                <LuckyVariationsGrid
                  jobId={art.id}
                  images={artImages}
                  zipUrl={art.zip_s3_url}
                  selectedVariations={art.original_request?.selected_variations as string[] | undefined}
                  onRegenerate={handleReloadImages}
                />
              ) : (
                <div className="text-center py-8 text-purple-200">
                  Nenhuma variação encontrada
                </div>
              )}
            </div>
          ) : (
            /* Modo Normal: Exibir imagem única */
            <>
              <div className="flex justify-center">
                <div 
                  className="relative rounded-lg overflow-hidden border border-purple-500/30 shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 transition-all" 
                  style={{ 
                    width: imageDimensions.width, 
                    height: imageDimensions.height, 
                    maxWidth: imageDimensions.maxWidth 
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <OptimizedImage 
                    key={art.id} 
                    src={art.s3_url || "/placeholder.svg"} 
                    alt="Generated art" 
                    className="w-full h-full object-contain touch-none" 
                    style={{
                      transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`,
                      transformOrigin: 'center',
                      transition: zoom === 1 ? 'transform 0.2s ease-out' : 'none'
                    }}
                    draggable={false}
                  />
                  {/* Indicador de zoom */}
                  {zoom > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {(zoom * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-4 text-xs sm:text-lg text-purple-200/80 mb-2 sm:mb-8">
            <div>Status: <span className="font-medium text-purple-100">{art.status}</span></div>
            <div>Dimensões: <span className="font-medium text-purple-100">{art.width}x{art.height}px</span></div>
            <div className="col-span-2 sm:col-span-1">Seed: <span className="font-medium text-purple-100">{art.seed}</span></div>
            {art.modo_kv && <div className="col-span-2 sm:col-span-3 text-xs sm:text-base text-purple-300">🖼️ Gerado com Modo Key Visual</div>}
            {isLuckyMode && <div className="col-span-2 sm:col-span-3 text-xs sm:text-base text-purple-300">🍀 Gerado com Modo Lucky ({artImages.length} variações)</div>}
          </div>

          <div className="flex gap-2 sm:gap-6 justify-center flex-wrap">
            <Button 
              onClick={() => navigate("/")} 
              variant="outline" 
              size="sm"
              className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black font-semibold text-sm sm:text-lg h-10 sm:h-14 px-4 sm:px-8"
            >
              Voltar
            </Button>
            {!isLuckyMode && (
              <Button 
                variant="outline" 
                onClick={handleReroll} 
                disabled={isRerolling} 
                size="sm"
                className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black font-semibold text-sm sm:text-lg h-10 sm:h-14 px-4 sm:px-8"
              >
                {isRerolling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Gerando...</span>
                    <span className="sm:hidden">Gen...</span>
                  </>
                ) : (
                  "Refazer"
                )}
              </Button>
            )}
            {/* 💜 Botão Modo Sonho da Sorte - apenas no modo normal */}
            {!isLuckyMode && (
              <Button
                variant="outline"
                onClick={() => setShowSonhoDialog(true)}
                disabled={isSonhoRegenerating}
                size="sm"
                className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black font-semibold text-sm sm:text-lg h-10 sm:h-14 px-4 sm:px-8"
              >
                {isSonhoRegenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Aplicando...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  "💜 Sonho da Sorte"
                )}
              </Button>
            )}
            {!isLuckyMode && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditPrompt("")
                  setIsEditing(true)
                }}
                size="sm"
                // disabled={isAnalyzing} // Removed
                className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black font-semibold text-sm sm:text-lg h-10 sm:h-14 px-4 sm:px-8"
              >
                <Edit2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Editar</span>
              </Button>
            )}
            {!isLuckyMode && (
              <Button 
                onClick={() => navigate(`/editor/${art.id}`)} 
                size="sm"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-2xl hover:shadow-purple-600/50 text-sm sm:text-lg font-semibold h-10 sm:h-14 px-4 sm:px-8"
              >
                <span className="hidden sm:inline">Continuar para Editor</span>
                <span className="sm:hidden">Editor</span>
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Dialog de Reroll com Variação Opcional */}
      <Dialog open={showRerollDialog} onOpenChange={setShowRerollDialog}>
        <DialogContent className="bg-slate-900 border-purple-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-purple-100">🔄 Refazer com Variação</DialogTitle>
            <DialogDescription className="text-purple-300">
              Digite uma mudança opcional ou deixe em branco para gerar uma variação pura. O sistema analisará a arte atual e gerará algo diferente!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-purple-300 font-semibold">Mudança (OPCIONAL)</label>
              <Textarea
                value={rerollPrompt}
                onChange={(e) => setRerollPrompt(e.target.value)}
                placeholder="Ex: mudar cor, diferente pose, outro cenário... (deixe em branco para variação pura)"
                className="min-h-24 bg-slate-800 border-purple-500/30 text-white placeholder:text-purple-300/50 resize-none focus:border-purple-500 focus:ring-purple-500"
                disabled={isRerolling}
              />
            </div>
            
            <div className="bg-purple-950/30 border border-purple-500/20 rounded-lg p-3 text-xs text-purple-200">
              <p className="font-semibold mb-1">💡 Como funciona:</p>
              <ul className="space-y-0.5 list-disc list-inside text-xs">
                <li>O sistema analisa a arte atual e descreve seus elementos</li>
                <li>Combina com sua mudança (se digitou) ou gera variação pura</li>
                <li>Usa novo seed = sempre gera algo diferente</li>
                <li>Clique refazer várias vezes para mais variações!</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRerollDialog(false)
                setRerollPrompt("")
              }}
              disabled={isRerolling}
              className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmReroll}
              disabled={isRerolling}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              {isRerolling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                "Gerar Variação"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição Simplificado */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-slate-900 border-purple-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-purple-100">✏️ Editar Arte</DialogTitle>
            <DialogDescription className="text-purple-300">
              Descreva a mudança que quer. O sistema analisa a imagem atual e regenera aplicando sua edição!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-purple-300 font-semibold">O que quer mudar?</label>
              <Textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder="Ex: mover para esquerda, virado para direita, mais para cima, cabelo rosa, cores mais vibrantes, menos escuro, objeto maior, etc."
                className="min-h-24 bg-slate-800 border-purple-500/30 text-white placeholder:text-purple-300/50 resize-none focus:border-purple-500 focus:ring-purple-500"
                disabled={isEditRegenerating}
              />
            </div>
            
            <div className="bg-purple-950/30 border border-purple-500/20 rounded-lg p-3 text-xs text-purple-200">
              <p className="font-semibold mb-1">💡 Exemplos de edições:</p>
              <ul className="space-y-0.5 list-disc list-inside text-xs">
                <li><strong>Posição (X,Y):</strong> "mover para esquerda", "mais para baixo", "virado para direita", "esquerda 30%", "cima 40%"</li>
                <li><strong>Aparência:</strong> "cabelo rosa", "roupas diferentes", "mais musculoso"</li>
                <li><strong>Cores:</strong> "mais colorido", "tons mais escuros", "cores pastel"</li>
                <li><strong>Tamanho:</strong> "objeto maior", "personagem menor", "expandir cena"</li>
                <li><strong>Ambiente:</strong> "em um castelo", "no deserto", "paisagem diferente"</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditing(false)
                setEditPrompt("")
              }}
              disabled={isEditRegenerating}
              className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEditPrompt}
              disabled={isEditRegenerating || !editPrompt.trim()}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              {isEditRegenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gerando...
                </>
              ) : (
                "Aplicar Mudança"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Modo Sonho da Sorte */}
      <Dialog open={showSonhoDialog} onOpenChange={setShowSonhoDialog}>
        <DialogContent className="bg-slate-900 border-purple-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-purple-100">💜 Modo Sonho da Sorte</DialogTitle>
            <DialogDescription className="text-purple-300">
              ✨ Transforma sua arte em roxo e amarelo (identidade Sonho da Sorte)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-950/30 border border-purple-500/20 rounded-lg p-4 text-sm text-purple-200">
              <p className="font-semibold mb-2">O que faz:</p>
              <ul className="space-y-1 list-disc list-inside text-sm">
                <li>Recolore a imagem com roxo (#7B4FB0) como cor dominante</li>
                <li>Adiciona amarelo (#FFD700) como destaques</li>
                <li>Mantém o sujeito principal reconhecível</li>
                <li>Aplica roxo no fundo, sombras e detalhes secundários</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-purple-300 font-semibold">Mudança adicional (opcional)</label>
              <Textarea
                value={sonhoPrompt}
                onChange={(e) => setSonhoPrompt(e.target.value)}
                placeholder="Digite uma mudança adicional ou deixe em branco para apenas aplicar roxo/amarelo. Ex: 'mais iluminado', 'expressão alegre', etc."
                className="min-h-20 bg-slate-800 border-purple-500/30 text-white placeholder:text-purple-300/50 resize-none focus:border-purple-500 focus:ring-purple-500"
                disabled={isSonhoRegenerating}
              />
            </div>
          </div>
 
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowSonhoDialog(false)
                setSonhoPrompt("")
              }}
              disabled={isSonhoRegenerating}
              className="border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10 text-black"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSonho}
              disabled={isSonhoRegenerating}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              {isSonhoRegenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aplicando...
                </>
              ) : (
                "Aplicar Sonho da Sorte"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}


